<template>
  <div class="features-container">
    <div>
      <b-navbar toggleable="lg" type="dark" variant="dark">
        <!-- Image and text -->
        <b-navbar-brand href="#">
          <img
            src="./../assets/stuff_to_do_logo.png"
            class="d-inline-block align-top logo"
            alt="Stuff To-Do logo"
          />
          Stuff To-Do
        </b-navbar-brand>

        <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

        <b-collapse id="nav-collapse" is-nav>
          <b-navbar-nav>
            <b-nav-item @click.prevent class="active">Features</b-nav-item>
          </b-navbar-nav>

          <!-- Right aligned nav items -->
          <b-navbar-nav class="ml-auto">
            <b-nav-item @click="navigateToSignInPage">Sign in</b-nav-item>
            <b-nav-item @click="navigateToSignUpPage">Sign up</b-nav-item>
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>
    </div>

    <div class="tagline-container">
      <h1 class="tagline">We’ve all got a ton of #StuffToDo</h1>
      <b-button @click="navigateToSignUpPage" class="browser-product-button"
        >Get Started</b-button
      >
      <b-button
        class="android-product-button"
        href="/apk/app-debug.apk"
        download
        >Download for Android</b-button
      >
    </div>

    <b-img
      fluid
      class="product-showcase"
      :src="require('../assets/landing_page/landing_page_1.png')"
      alt="Landing page image 1"
    ></b-img>

    <div class="section-header">
      <h1>Say hello to the next generation of to-do lists</h1>
      <p>
        Stuff To-Do brings game-changing features that’ll totally reinvent the
        way you get things done
      </p>
    </div>

    <div class="individual-feature-container">
      <b-row>
        <b-col sm="12" lg="6">
          <b-img
            fluid
            class="individual-feature-image-height"
            :src="require('../assets/landing_page/landing_page_2.png')"
            alt="Landing page image 2"
          ></b-img>
        </b-col>
        <b-col></b-col>
        <b-col sm="12" lg="4" class="individual-feature-text">
          <h2>Your to-do list, your way</h2>
          <p>Personal, simple, and ready when you are</p>
          <hr />
          <div class="individual-feature-description-text">
            <p>
              Look familiar? This might take you back to the early days — when
              using a to-do list felt fresh, simple, and satisfying. With Stuff
              To-Do, that classic experience is still here — now with a touch of
              magic.
            </p>
            <p>Out-of-the-box magic with Stuff To-Do:</p>
            <ul>
              <li>
                Your tasks are automagically synced with the cloud — no fuss
              </li>
              <li>
                Access your to-do list on any platform and always see the latest
                updates
              </li>
              <li>
                Never miss a task with built-in reminders for every item *
              </li>
              <li>
                Add images to tasks to bring more context and clarity — because
                a picture really is worth a thousand words
              </li>
            </ul>
            <p>
              * Reminder feature available on the mobile app version of Stuff
              To-Do
            </p>
          </div>
        </b-col>
        <b-col></b-col>
      </b-row>
    </div>

    <div class="individual-feature-container">
      <b-row>
        <b-col sm="12" lg="6">
          <b-img
            fluid
            :src="require('../assets/landing_page/landing_page_3.png')"
            alt="Landing page image 3"
          ></b-img>
        </b-col>
        <b-col></b-col>
        <b-col sm="12" lg="4" class="individual-feature-text">
          <h2>Your shared to-do list</h2>
          <p>The all-in-one space for getting things done — together</p>
          <hr />
          <div class="individual-feature-description-text">
            <p>
              Sound familiar? You’re tackling a bunch of tasks with your team —
              but someone misses an update, forgets their task, or has no clue
              what’s going on. Or maybe your family swore they’d grab the milk…
              but came home with snacks instead. Yeah, we’ve all been there.
            </p>
            <p>
              Collaboration can get messy. That’s why Stuff To-Do is built with
              powerful features to keep everyone on the same page:
            </p>
            <ul>
              <li>
                Assign tasks to specific collaborators, so everyone knows who’s
                doing what
              </li>
              <li>
                Filter with ease — see everything or just what’s assigned to you
              </li>
              <li>
                View full task details, including who’s responsible for each
                item
              </li>
            </ul>

            <p>
              With Stuff To-Do, collaboration feels seamless — so you and your
              team can focus on what really matters: achieving big things,
              together.
            </p>
          </div>
        </b-col>
        <b-col></b-col>
      </b-row>
    </div>

    <div class="individual-feature-container">
      <b-row>
        <b-col sm="12" lg="6">
          <b-img
            fluid
            class="individual-feature-image-height"
            :src="require('../assets/landing_page/landing_page_4.png')"
            alt="Landing page image 4"
          ></b-img>
        </b-col>
        <b-col></b-col>
        <b-col sm="12" lg="4" class="individual-feature-text">
          <h2>Smarter collaboration, zero friction</h2>
          <p>Easily manage your teammates and keep everything on track</p>
          <hr />
          <div class="individual-feature-description-text">
            <p>
              One of the biggest challenges in managing a collaborative to-do
              list? Lack of control over collaborator permissions. Too often,
              there’s no way to manage who can do what — which leads to chaos.
              That’s why Stuff To-Do comes with built-in role management to keep
              things organized and secure.
            </p>
            <p>Here’s how it works:</p>
            <ul>
              <li>
                Administrator - Full access and control. Create, edit, delete,
                comment on tasks — and manage collaborators (add or remove them)
              </li>
              <li>
                Member - Limited access. View task details and leave comments,
                but no editing or collaborator management
              </li>
            </ul>
          </div>
        </b-col>
        <b-col></b-col>
      </b-row>
    </div>

    <div class="individual-feature-container">
      <b-row>
        <b-col sm="12" lg="6">
          <b-img
            fluid
            :src="require('../assets/landing_page/landing_page_5.png')"
            alt="Landing page image 5"
          ></b-img>
        </b-col>
        <b-col></b-col>
        <b-col sm="12" lg="4" class="individual-feature-text">
          <h2>Collaboration, Reimagined</h2>
          <p>A fresh take on how teams get things done</p>
          <hr />
          <div class="individual-feature-description-text">
            <p>
              Let’s face it — communication is often the hardest part of
              collaboration. Misunderstandings, confusion, and lack of clarity
              can easily derail even the best of plans.
            </p>
            <p>
              To help solve that, every collaborative task in Stuff To-Do comes
              with its own built-in comment section — like a mini chat room just
              for that task. Collaborators can easily share updates, ask
              questions, and stay aligned, all in one place.
            </p>
            <p>
              As we’ve said before, collaboration is at the heart of Stuff To-Do
              — and with features like this, your team can focus less on fixing
              miscommunication and more on reaching goals together.
            </p>
          </div>
        </b-col>
        <b-col></b-col>
      </b-row>
    </div>

    <div class="section-header">
      <h1>From the Founder</h1>
      <p>
        A glimpse into the vision and purpose behind Stuff To-Do — and the
        journey it’s built to support
      </p>
    </div>

    <div class="words-from-the-founder-section">
      <b-row>
        <b-col></b-col>
        <b-col cols="12" lg="5">
          <div class="words-from-the-founder-text">
            <p>
              I’ve always been a power user of to-do lists. Since early on in
              life, I’ve relied on them to stay organized and get things done.
              Over the years, I’ve tried countless task management apps. Most
              were solid — but I kept running into the same issue: they just
              weren’t built for collaboration.
            </p>
            <p>
              That’s why, as a software engineer, I decided to build something
              different. Stuff To-Do was created from the ground up with
              collaboration at its core — designed not just for personal
              productivity, but for working together seamlessly.
            </p>
            <p>
              I hope this passion project helps you tackle everything from solo
              goals to team milestones — with less stress and more clarity.
            </p>
            <p class="founder-name">
              - Yoseph Martin Christian, founder of Stuff To-Do (2020)
            </p>
          </div>
        </b-col>
        <b-col cols="12" lg="5">
          <b-img
            class="founder-image-height"
            :src="require('../assets/founder_photo.jpg')"
            alt="Founder photo"
          ></b-img>
        </b-col>
        <b-col></b-col>
      </b-row>
    </div>

    <div class="footer">
      <p>© 2020–2025 Stuff To-Do. Developed by Yoseph Martin Christian.</p>
    </div>
  </div>
</template>

<script>
export default {
  methods: {
    navigateToSignInPage() {
      this.$router.push({ name: "Sign In" });
    },
    navigateToSignUpPage() {
      this.$router.push({ name: "Sign Up" });
    },
  },
};
</script>

<style scoped>
.features-container {
  text-align: center;
}

.navbar.navbar-dark.bg-dark {
  background-color: #212441 !important;
}

.logo {
  width: 1.5em;
  margin-right: 0.25em;
}

.tagline-container {
  text-align: center;
  margin: 4em auto 0;
}

.tagline {
  font-size: 3em;
}

.browser-product-button {
  display: block;
  font-weight: bold;
  background-color: #212441;
  font-size: 1.25em;
  padding: 0.75em;
  margin: 1em auto 0;
}

.browser-product-button:hover {
  font-weight: bold;
  background-color: #ffffff;
  color: #212441;
  font-size: 1.25em;
  padding: 0.75em;
}

.android-product-button {
  display: inline-block;
  font-weight: bold;
  background-color: #ffffff;
  color: #212441;
  font-size: 1.25em;
  padding: 0.75em;
  margin: 1em auto;
}

.android-product-button:hover {
  font-weight: bold;
  background-color: #212441;
  color: #ffffff;
  font-size: 1.25em;
  padding: 0.75em;
}

.download-link-style {
  color: #212441;
}

.download-link-style:hover {
  color: #ffffff;
  text-decoration: none;
}

.product-showcase {
  margin-bottom: 2em;
}

.section-header {
  background-color: #212441;
  color: white;
  padding: 4em;
}

.individual-feature-container {
  margin: 2em;
  padding: 2em;
  border: 2px solid #212441;
}

.individual-feature-text {
  padding-top: 1em;
}

.individual-feature-description-text {
  text-align: justify;
}

.individual-feature-image-height {
  max-height: 550px;
}

.words-from-the-founder-section {
  padding: 2em 4em 0;
}

.words-from-the-founder-text {
  vertical-align: middle;
  text-align: justify;
}

.founder-name {
  font-weight: bold;
}

.founder-image-height {
  max-height: 500px;
}

.footer {
  background-color: #212441;
  color: white;
  padding: 0.5em;
}

.footer p {
  font-weight: bold;
  margin: 0;
}
</style>
